import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { BarChart, Bar, XAxis, YAxis, Tooltip, Cell, ResponsiveContainer } from 'recharts';
import Header from '../components/layout/Header';
import Panel from '../components/common/Panel';
// import MetricCard from '../components/common/MetricCard';
import {
  getCampaign,
  getCampaignContacts,
  getCampaigns,
  removeContactFromCampaign,
  addContactsToCampaign,
  copyContactsToCampaign,
  updateCampaignContactStatus,
  getContacts,
  createActivity,
  getCampaignDashboard,
  rankProspects,
  generateDrafts,
  scheduleMeeting,
} from '../services/api';

interface Campaign {
  id: string;
  name: string;
  description?: string;
  campaign_type: string;
  status: string;
  recipient_count?: number;
  tags?: string[];
  created_at: string;
  target_audience?: Record<string, unknown>;
}

interface CampaignContact {
  contact_id: string;
  first_name?: string;
  last_name?: string;
  email?: string;
  title?: string;
  status?: string;
}

type Tab = 'overview' | 'contacts' | 'analytics';

export default function CampaignDetailPage() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [campaign, setCampaign] = useState<Campaign | null>(null);
  const [contacts, setContacts] = useState<CampaignContact[]>([]);
  // const [dashboard, setDashboard] = useState<Record<string, unknown> | null>(null);
  const [tab, setTab] = useState<Tab>('overview');
  const [loading, setLoading] = useState(true);
  const [actionMsg, setActionMsg] = useState('');

  // Quick log state
  const [logContact, setLogContact] = useState<CampaignContact | null>(null);
  const [logType, setLogType] = useState<'call' | 'email' | 'note'>('note');
  const [logSubject, setLogSubject] = useState('');
  const [logSaving, setLogSaving] = useState(false);

  // Copy contacts modal state
  const [showCopyModal, setShowCopyModal] = useState(false);
  const [allCampaigns, setAllCampaigns] = useState<Array<{ id: string; name: string }>>([]);
  const [targetCampaignId, setTargetCampaignId] = useState('');
  const [copying, setCopying] = useState(false);

  // Add contacts modal state
  const [showAddModal, setShowAddModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<Array<{ id: string; first_name?: string; last_name?: string; email?: string; title?: string }>>([]);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [addingContacts, setAddingContacts] = useState(false);
  const [searching, setSearching] = useState(false);

  // Intelligence state
  const [intelligence, setIntelligence] = useState<Record<string, unknown> | null>(null);
  const [intelligenceLoading, setIntelligenceLoading] = useState(false);
  const [rankingLoading, setRankingLoading] = useState(false);
  const [draftsLoading, setDraftsLoading] = useState(false);

  // Schedule meeting modal state
  const [meetingContact, setMeetingContact] = useState<CampaignContact | null>(null);
  const [meetingType, setMeetingType] = useState('phone');
  const [meetingDate, setMeetingDate] = useState('');
  const [meetingDuration, setMeetingDuration] = useState(30);
  const [meetingSubject, setMeetingSubject] = useState('');
  const [meetingNotes, setMeetingNotes] = useState('');
  const [meetingSaving, setMeetingSaving] = useState(false);

  useEffect(() => {
    if (id) loadCampaign();
  }, [id]);

  useEffect(() => {
    if (id && (tab === 'contacts' || tab === 'analytics')) loadContacts();
  }, [id, tab]);

  useEffect(() => {
    if (id && tab === 'overview') loadIntelligence();
  }, [id, tab]);

  async function loadCampaign() {
    try {
      const data = await getCampaign(id!);
      setCampaign(data);
    } catch (err) {
      console.error('Failed to load campaign:', err);
    } finally {
      setLoading(false);
    }
  }

  async function loadContacts() {
    try {
      const data = await getCampaignContacts(id!);
      const items = Array.isArray(data) ? data : data?.contacts || data?.items || [];
      setContacts(items);
    } catch (err) {
      console.error('Failed to load contacts:', err);
    }
  }

  async function loadIntelligence() {
    setIntelligenceLoading(true);
    try {
      const data = await getCampaignDashboard(id!);
      setIntelligence(data);
    } catch (err) {
      console.error('Failed to load intelligence dashboard:', err);
    } finally {
      setIntelligenceLoading(false);
    }
  }

  async function handleRankProspects() {
    setRankingLoading(true);
    try {
      await rankProspects(id!);
      setActionMsg('Prospects ranked');
      setTimeout(() => setActionMsg(''), 3000);
      loadContacts();
    } catch (err) {
      console.error('Failed to rank prospects:', err);
      setActionMsg('Failed to rank prospects');
      setTimeout(() => setActionMsg(''), 3000);
    } finally {
      setRankingLoading(false);
    }
  }

  async function handleGenerateDrafts() {
    setDraftsLoading(true);
    try {
      await generateDrafts(id!);
      setActionMsg('Drafts generated');
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      console.error('Failed to generate drafts:', err);
      setActionMsg('Failed to generate drafts');
      setTimeout(() => setActionMsg(''), 3000);
    } finally {
      setDraftsLoading(false);
    }
  }

  async function handleRemoveContact(contactId: string) {
    try {
      await removeContactFromCampaign(id!, contactId);
      setContacts(prev => prev.filter(c => c.contact_id !== contactId));
    } catch (err) {
      console.error('Failed to remove contact:', err);
    }
  }

  const campaignStatusOptions = ['added', 'contacted', 'opened', 'replied', 'meeting_scheduled', 'converted'];

  async function handleStatusChange(contactId: string, newStatus: string) {
    try {
      await updateCampaignContactStatus(id!, contactId, newStatus);
      setContacts(prev => prev.map(c =>
        c.contact_id === contactId ? { ...c, status: newStatus } : c
      ));
      setActionMsg(`Status updated to "${newStatus}"`);
      setTimeout(() => setActionMsg(''), 2000);
    } catch (err) {
      console.error('Failed to update contact status:', err);
    }
  }

  async function handleLogActivity() {
    if (!logContact || !logSubject.trim()) return;
    setLogSaving(true);
    try {
      await createActivity({
        type: logType,
        subject: logSubject,
        contact_id: logContact.contact_id,
        description: `Campaign: ${campaign?.name}`,
      });
      setLogContact(null);
      setLogSubject('');
      setActionMsg('Activity logged');
      setTimeout(() => setActionMsg(''), 2000);
    } catch (err) {
      console.error('Failed to log activity:', err);
    } finally {
      setLogSaving(false);
    }
  }

  function openMeetingModal(contact: CampaignContact) {
    setMeetingContact(contact);
    setMeetingType('phone');
    setMeetingDate('');
    setMeetingDuration(30);
    setMeetingSubject('');
    setMeetingNotes('');
  }

  async function handleScheduleMeeting() {
    if (!meetingContact || !meetingDate) return;
    setMeetingSaving(true);
    try {
      await scheduleMeeting(id!, meetingContact.contact_id, {
        meeting_type: meetingType,
        scheduled_date: new Date(meetingDate).toISOString(),
        duration: meetingDuration,
        subject: meetingSubject || 'Meeting',
        notes: meetingNotes,
      });
      // Update local contact status
      setContacts(prev => prev.map(c =>
        c.contact_id === meetingContact.contact_id ? { ...c, status: 'meeting_scheduled' } : c
      ));
      setMeetingContact(null);
      setActionMsg('Meeting scheduled');
      setTimeout(() => setActionMsg(''), 3000);
    } catch (err) {
      console.error('Failed to schedule meeting:', err);
      setActionMsg('Failed to schedule meeting');
      setTimeout(() => setActionMsg(''), 3000);
    } finally {
      setMeetingSaving(false);
    }
  }

  async function handleOpenCopyModal() {
    setShowCopyModal(true);
    setTargetCampaignId('');
    try {
      const data = await getCampaigns();
      const items = Array.isArray(data) ? data : data?.items || [];
      // Exclude the current campaign from the list
      setAllCampaigns(items.filter((c: { id: string }) => c.id !== id));
    } catch (err) {
      console.error('Failed to load campaigns:', err);
    }
  }

  async function handleCopyContacts() {
    if (!targetCampaignId || !id) return;
    setCopying(true);
    try {
      const result = await copyContactsToCampaign(id, targetCampaignId);
      setActionMsg(`Copied ${result.copied} contact(s) to campaign`);
      setTimeout(() => setActionMsg(''), 3000);
      setShowCopyModal(false);
    } catch (err) {
      console.error('Failed to copy contacts:', err);
      setActionMsg('Failed to copy contacts');
      setTimeout(() => setActionMsg(''), 3000);
    } finally {
      setCopying(false);
    }
  }

  async function handleSearchContacts() {
    if (!searchTerm.trim()) return;
    setSearching(true);
    try {
      const data = await getContacts({ query: searchTerm, size: '20' });
      const items = Array.isArray(data) ? data : data?.items || data?.contacts || [];
      setSearchResults(items);
    } catch (err) {
      console.error('Failed to search contacts:', err);
    } finally {
      setSearching(false);
    }
  }

  function toggleSelected(contactId: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(contactId)) next.delete(contactId);
      else next.add(contactId);
      return next;
    });
  }

  async function handleAddSelected() {
    if (selectedIds.size === 0 || !id) return;
    setAddingContacts(true);
    try {
      const result = await addContactsToCampaign(id, Array.from(selectedIds));
      setActionMsg(`Added ${result.added} contact(s) to campaign`);
      setTimeout(() => setActionMsg(''), 3000);
      setShowAddModal(false);
      setSearchTerm('');
      setSearchResults([]);
      setSelectedIds(new Set());
      loadContacts();
    } catch (err) {
      console.error('Failed to add contacts:', err);
    } finally {
      setAddingContacts(false);
    }
  }

  if (loading) return <div className="p-8" style={{ color: 'var(--text-muted)' }}>Loading...</div>;
  if (!campaign) return <div className="p-8" style={{ color: 'var(--text-muted)' }}>Campaign not found</div>;

  const tabs: { key: Tab; label: string }[] = [
    { key: 'overview', label: 'Overview' },
    { key: 'contacts', label: `Contacts${contacts.length ? ` (${contacts.length})` : ''}` },
    { key: 'analytics', label: 'Analytics' },
  ];

  return (
    <div className="page-enter">
      <Header
        title={campaign.name}
        subtitle={`${campaign.campaign_type} · ${campaign.status}`}
      />
      <div className="p-4 md:p-8">
        {/* Back button */}
        <button
          onClick={() => navigate('/campaigns')}
          className="text-xs mb-4 hover:underline"
          style={{ color: 'var(--netrun-green)' }}
        >
          ← All Campaigns
        </button>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 border-b" style={{ borderColor: 'var(--border-primary)' }}>
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className="px-4 py-2 text-sm font-display transition-colors"
              style={{
                color: tab === t.key ? 'var(--netrun-green)' : 'var(--text-secondary)',
                borderBottom: tab === t.key ? '2px solid var(--netrun-green)' : '2px solid transparent',
              }}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Action message */}
        {actionMsg && (
          <div className="mb-4 text-sm px-3 py-2 rounded" style={{ background: 'var(--bg-tertiary)', color: 'var(--netrun-green)' }}>
            {actionMsg}
          </div>
        )}

        {/* Overview Tab */}
        {tab === 'overview' && (
          <div className="space-y-6">
            <div className="grid gap-4 md:grid-cols-3">
              <Panel title="Status">
                <span className="text-lg font-display" style={{ color: 'var(--netrun-green)' }}>{campaign.status}</span>
              </Panel>
              <Panel title="Type">
                <span className="text-lg font-display" style={{ color: 'var(--text-primary)' }}>{campaign.campaign_type}</span>
              </Panel>
              <Panel title="Contacts">
                <span className="text-lg font-display" style={{ color: 'var(--text-primary)' }}>{campaign.recipient_count || contacts.length || 0}</span>
              </Panel>
            </div>
            {campaign.description && (
              <Panel title="Description">
                <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>{campaign.description}</p>
              </Panel>
            )}
            {campaign.tags && campaign.tags.length > 0 && (
              <Panel title="Tags">
                <div className="flex gap-2 flex-wrap">
                  {campaign.tags.map(t => (
                    <span key={t} className="text-xs px-2 py-1 rounded" style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}>{t}</span>
                  ))}
                </div>
              </Panel>
            )}
            <Panel title="Details">
              <div className="space-y-2 text-sm" style={{ color: 'var(--text-secondary)' }}>
                <div>Created: {new Date(campaign.created_at).toLocaleString()}</div>
                <div>ID: <code className="text-xs">{campaign.id}</code></div>
              </div>
            </Panel>
            <Panel title="Intelligence">
              {intelligenceLoading ? (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>Loading intelligence data...</p>
              ) : intelligence ? (
                <div className="space-y-2 text-sm">
                  {[
                    { label: 'Total Contacts', key: 'total_contacts' },
                    { label: 'Enriched Contacts', key: 'enriched_contacts' },
                    { label: 'High-Fit Contacts', key: 'high_fit_contacts' },
                    { label: 'Drafts Generated', key: 'drafts_generated' },
                  ].map(row => (
                    <div key={row.key} className="flex justify-between py-1.5 px-2 rounded" style={{ background: 'var(--bg-tertiary)' }}>
                      <span style={{ color: 'var(--text-secondary)' }}>{row.label}</span>
                      <span className="font-display" style={{ color: 'var(--text-primary)' }}>
                        {intelligence[row.key] !== undefined ? String(intelligence[row.key]) : '—'}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No intelligence data available</p>
              )}
            </Panel>
          </div>
        )}

        {/* Contacts Tab */}
        {tab === 'contacts' && (
          <div>
            <div className="flex justify-end gap-2 mb-4 flex-wrap">
              <button
                onClick={handleRankProspects}
                disabled={rankingLoading}
                className="text-xs px-4 py-2 rounded font-display hover:opacity-90"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--netrun-green)', border: '1px solid var(--netrun-green)', opacity: rankingLoading ? 0.6 : 1 }}
              >
                {rankingLoading ? 'Ranking...' : 'Rank Prospects'}
              </button>
              <button
                onClick={handleGenerateDrafts}
                disabled={draftsLoading}
                className="text-xs px-4 py-2 rounded font-display hover:opacity-90"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--netrun-green)', border: '1px solid var(--netrun-green)', opacity: draftsLoading ? 0.6 : 1 }}
              >
                {draftsLoading ? 'Generating...' : 'Generate Drafts'}
              </button>
              <button
                onClick={handleOpenCopyModal}
                className="text-xs px-4 py-2 rounded font-display hover:opacity-90"
                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
              >
                Copy All to...
              </button>
              <button
                onClick={() => setShowAddModal(true)}
                className="text-xs px-4 py-2 rounded font-display hover:opacity-90"
                style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)' }}
              >
                + Add Contacts
              </button>
            </div>
            {contacts.length === 0 ? (
              <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No contacts in this campaign</p>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-primary)' }}>
                      <th className="text-left py-2 px-3 font-display" style={{ color: 'var(--text-secondary)' }}>Name</th>
                      <th className="text-left py-2 px-3 font-display" style={{ color: 'var(--text-secondary)' }}>Email</th>
                      <th className="text-left py-2 px-3 font-display" style={{ color: 'var(--text-secondary)' }}>Title</th>
                      <th className="text-left py-2 px-3 font-display" style={{ color: 'var(--text-secondary)' }}>Status</th>
                      <th className="text-right py-2 px-3 font-display" style={{ color: 'var(--text-secondary)' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {contacts.map((c) => (
                      <tr key={c.contact_id} style={{ borderBottom: '1px solid var(--border-primary)' }}>
                        <td className="py-2 px-3" style={{ color: 'var(--text-primary)' }}>
                          {c.first_name} {c.last_name}
                        </td>
                        <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>{c.email || '—'}</td>
                        <td className="py-2 px-3" style={{ color: 'var(--text-secondary)' }}>{c.title || '—'}</td>
                        <td className="py-2 px-3">
                          <select
                            value={c.status || 'added'}
                            onChange={e => handleStatusChange(c.contact_id, e.target.value)}
                            className="text-xs px-2 py-1 rounded cursor-pointer"
                            style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)', border: '1px solid var(--border-primary)' }}
                          >
                            {campaignStatusOptions.map(s => (
                              <option key={s} value={s}>{s.replace('_', ' ')}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-2 px-3 text-right">
                          <div className="flex gap-1 justify-end">
                            {(['call', 'email', 'note'] as const).map(type => (
                              <button
                                key={type}
                                onClick={() => { setLogContact(c); setLogType(type); setLogSubject(''); }}
                                className="text-[10px] px-2 py-1 rounded hover:opacity-80"
                                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
                              >
                                {type === 'call' ? 'Log Call' : type === 'email' ? 'Log Email' : 'Note'}
                              </button>
                            ))}
                            <button
                              onClick={() => openMeetingModal(c)}
                              className="text-[10px] px-2 py-1 rounded hover:opacity-80"
                              title="Schedule Meeting"
                              style={{ background: 'var(--bg-tertiary)', color: 'var(--text-secondary)' }}
                            >
                              Schedule
                            </button>
                            <button
                              onClick={() => handleRemoveContact(c.contact_id)}
                              className="text-[10px] px-2 py-1 rounded hover:opacity-80"
                              style={{ color: '#ef4444' }}
                            >
                              ✕
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Add Contacts Modal */}
            {showAddModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => setShowAddModal(false)}>
                <div className="rounded-lg p-6 w-full max-w-lg max-h-[80vh] flex flex-col slide-up" style={{ background: '#141414', border: '1px solid var(--border-primary)' }} onClick={e => e.stopPropagation()}>
                  <h3 className="text-sm font-display font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Add Contacts to Campaign
                  </h3>
                  <div className="flex gap-2 mb-4">
                    <input
                      type="text"
                      placeholder="Search contacts by name or email..."
                      value={searchTerm}
                      onChange={e => setSearchTerm(e.target.value)}
                      onKeyDown={e => e.key === 'Enter' && handleSearchContacts()}
                      autoFocus
                      className="flex-1 px-3 py-2 rounded text-sm"
                      style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                    />
                    <button
                      onClick={handleSearchContacts}
                      disabled={searching || !searchTerm.trim()}
                      className="text-xs px-3 py-2 rounded font-display"
                      style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', opacity: searching ? 0.6 : 1 }}
                    >
                      {searching ? 'Searching...' : 'Search'}
                    </button>
                  </div>
                  <div className="flex-1 overflow-y-auto mb-4" style={{ maxHeight: '400px' }}>
                    {searchResults.length === 0 ? (
                      <p className="text-xs py-4 text-center" style={{ color: 'var(--text-muted)' }}>
                        {searchTerm ? 'No results. Try a different search.' : 'Type a name or email to search.'}
                      </p>
                    ) : (
                      searchResults.map(c => {
                        const isAlready = contacts.some(cc => cc.contact_id === c.id);
                        return (
                          <label
                            key={c.id}
                            className="flex items-center gap-3 px-3 py-2 rounded cursor-pointer hover:opacity-80"
                            style={{
                              borderBottom: '1px solid var(--border-primary)',
                              opacity: isAlready ? 0.5 : 1,
                            }}
                          >
                            <input
                              type="checkbox"
                              checked={selectedIds.has(c.id)}
                              onChange={() => toggleSelected(c.id)}
                              disabled={isAlready}
                              className="accent-[var(--netrun-green)]"
                            />
                            <div className="flex-1 min-w-0">
                              <div className="text-sm truncate" style={{ color: 'var(--text-primary)' }}>
                                {c.first_name} {c.last_name}
                                {isAlready && <span className="ml-2 text-[10px]" style={{ color: 'var(--text-muted)' }}>(already in campaign)</span>}
                              </div>
                              <div className="text-xs truncate" style={{ color: 'var(--text-secondary)' }}>
                                {c.email || '—'}{c.title ? ` · ${c.title}` : ''}
                              </div>
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                  <div className="flex gap-2 justify-between items-center">
                    <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                      {selectedIds.size > 0 ? `${selectedIds.size} selected` : ''}
                    </span>
                    <div className="flex gap-2">
                      <button onClick={() => setShowAddModal(false)} className="text-xs px-3 py-1.5 rounded"
                        style={{ color: 'var(--text-secondary)' }}>Cancel</button>
                      <button
                        onClick={handleAddSelected}
                        disabled={addingContacts || selectedIds.size === 0}
                        className="text-xs px-3 py-1.5 rounded font-display"
                        style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', opacity: addingContacts || selectedIds.size === 0 ? 0.6 : 1 }}
                      >
                        {addingContacts ? 'Adding...' : `Add Selected (${selectedIds.size})`}
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Copy Contacts Modal */}
            {showCopyModal && (
              <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => setShowCopyModal(false)}>
                <div className="rounded-lg p-6 w-full max-w-sm slide-up" style={{ background: '#141414', border: '1px solid var(--border-primary)' }} onClick={e => e.stopPropagation()}>
                  <h3 className="text-sm font-display font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Copy Contacts to Campaign
                  </h3>
                  <p className="text-xs mb-3" style={{ color: 'var(--text-secondary)' }}>
                    Copy all {contacts.length} contact(s) from this campaign to another.
                  </p>
                  <select
                    value={targetCampaignId}
                    onChange={e => setTargetCampaignId(e.target.value)}
                    className="w-full px-3 py-2 rounded text-sm mb-4"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                  >
                    <option value="">Select a campaign...</option>
                    {allCampaigns.map(c => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setShowCopyModal(false)} className="text-xs px-3 py-1.5 rounded"
                      style={{ color: 'var(--text-secondary)' }}>Cancel</button>
                    <button
                      onClick={handleCopyContacts}
                      disabled={copying || !targetCampaignId}
                      className="text-xs px-3 py-1.5 rounded font-display"
                      style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', opacity: copying || !targetCampaignId ? 0.6 : 1 }}
                    >
                      {copying ? 'Copying...' : 'Copy Contacts'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Quick Log Modal */}
            {logContact && (
              <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => setLogContact(null)}>
                <div className="rounded-lg p-6 w-full max-w-md slide-up" style={{ background: '#141414', border: '1px solid var(--border-primary)' }} onClick={e => e.stopPropagation()}>
                  <h3 className="text-sm font-display font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Log {logType} — {logContact.first_name} {logContact.last_name}
                  </h3>
                  <input
                    type="text"
                    placeholder="Subject"
                    value={logSubject}
                    onChange={e => setLogSubject(e.target.value)}
                    autoFocus
                    className="w-full px-3 py-2 rounded text-sm mb-4"
                    style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                  />
                  <div className="flex gap-2 justify-end">
                    <button onClick={() => setLogContact(null)} className="text-xs px-3 py-1.5 rounded"
                      style={{ color: 'var(--text-secondary)' }}>Cancel</button>
                    <button onClick={handleLogActivity} disabled={logSaving || !logSubject.trim()} className="text-xs px-3 py-1.5 rounded font-display"
                      style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', opacity: logSaving ? 0.6 : 1 }}>
                      {logSaving ? 'Saving...' : 'Save'}
                    </button>
                  </div>
                </div>
              </div>
            )}

            {/* Schedule Meeting Modal */}
            {meetingContact && (
              <div className="fixed inset-0 z-50 flex items-center justify-center" style={{ background: 'rgba(0,0,0,0.75)' }} onClick={() => setMeetingContact(null)}>
                <div className="rounded-lg p-6 w-full max-w-md slide-up" style={{ background: '#141414', border: '1px solid var(--border-primary)' }} onClick={e => e.stopPropagation()}>
                  <h3 className="text-sm font-display font-semibold mb-4" style={{ color: 'var(--text-primary)' }}>
                    Schedule Meeting — {meetingContact.first_name} {meetingContact.last_name}
                  </h3>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Meeting Type</label>
                      <select
                        value={meetingType}
                        onChange={e => setMeetingType(e.target.value)}
                        className="w-full px-3 py-2 rounded text-sm"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                      >
                        <option value="phone">Phone</option>
                        <option value="video">Video Call</option>
                        <option value="in_person">In Person</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Date & Time</label>
                      <input
                        type="datetime-local"
                        value={meetingDate}
                        onChange={e => setMeetingDate(e.target.value)}
                        className="w-full px-3 py-2 rounded text-sm"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Duration</label>
                      <select
                        value={meetingDuration}
                        onChange={e => setMeetingDuration(Number(e.target.value))}
                        className="w-full px-3 py-2 rounded text-sm"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                      >
                        <option value={15}>15 minutes</option>
                        <option value={30}>30 minutes</option>
                        <option value={45}>45 minutes</option>
                        <option value={60}>60 minutes</option>
                      </select>
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Subject</label>
                      <input
                        type="text"
                        placeholder="Meeting subject"
                        value={meetingSubject}
                        onChange={e => setMeetingSubject(e.target.value)}
                        className="w-full px-3 py-2 rounded text-sm"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                      />
                    </div>
                    <div>
                      <label className="block text-xs mb-1" style={{ color: 'var(--text-secondary)' }}>Notes (optional)</label>
                      <textarea
                        placeholder="Meeting notes..."
                        value={meetingNotes}
                        onChange={e => setMeetingNotes(e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 rounded text-sm resize-none"
                        style={{ background: 'var(--bg-tertiary)', border: '1px solid var(--border-primary)', color: 'var(--text-primary)' }}
                      />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end mt-4">
                    <button onClick={() => setMeetingContact(null)} className="text-xs px-3 py-1.5 rounded"
                      style={{ color: 'var(--text-secondary)' }}>Cancel</button>
                    <button
                      onClick={handleScheduleMeeting}
                      disabled={meetingSaving || !meetingDate}
                      className="text-xs px-3 py-1.5 rounded font-display"
                      style={{ background: 'var(--netrun-green)', color: 'var(--netrun-black)', opacity: meetingSaving || !meetingDate ? 0.6 : 1 }}
                    >
                      {meetingSaving ? 'Scheduling...' : 'Schedule Meeting'}
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Analytics Tab */}
        {tab === 'analytics' && (() => {
          const stages = ['added', 'contacted', 'opened', 'replied', 'meeting_scheduled', 'converted'] as const;
          const stageCounts = stages.map(s => ({
            stage: s,
            count: contacts.filter(c => (c.status || 'added') === s).length,
          }));
          void Math.max(...stageCounts.map(s => s.count), 1);

          const stageColors: Record<string, string> = {
            added: '#6b7280',
            contacted: '#3b82f6',
            opened: '#8b5cf6',
            replied: '#f59e0b',
            meeting_scheduled: '#f97316',
            converted: '#22c55e',
          };

          const stageLabels: Record<string, string> = {
            added: 'Added',
            contacted: 'Contacted',
            opened: 'Opened',
            replied: 'Replied',
            meeting_scheduled: 'Meeting Scheduled',
            converted: 'Converted',
          };

          const totalContacts = contacts.length;
          const contactedCount = contacts.filter(c => {
            const s = c.status || 'added';
            return s !== 'added';
          }).length;
          const respondedCount = contacts.filter(c => {
            const s = c.status || 'added';
            return ['replied', 'meeting_scheduled', 'converted'].includes(s);
          }).length;
          const convertedCount = contacts.filter(c => (c.status || 'added') === 'converted').length;

          const metrics = [
            { label: 'Total Contacts', value: totalContacts, color: 'var(--text-primary)' },
            { label: 'Contacted', value: contactedCount, color: '#3b82f6' },
            { label: 'Responded', value: respondedCount, color: '#f59e0b' },
            { label: 'Converted', value: convertedCount, color: '#22c55e' },
          ];

          const statusMap: Record<string, number> = {};
          contacts.forEach(c => {
            const s = c.status || 'added';
            statusMap[s] = (statusMap[s] || 0) + 1;
          });

          return (
            <div className="space-y-6">
              {/* Metric Cards */}
              <div className="grid gap-4 grid-cols-2 md:grid-cols-4">
                {metrics.map(m => (
                  <Panel key={m.label} title={m.label}>
                    <span className="text-2xl font-display" style={{ color: m.color }}>{m.value}</span>
                    {totalContacts > 0 && m.label !== 'Total Contacts' && (
                      <span className="text-xs ml-2" style={{ color: 'var(--text-muted)' }}>
                        ({Math.round((m.value / totalContacts) * 100)}%)
                      </span>
                    )}
                  </Panel>
                ))}
              </div>

              {/* Conversion Funnel */}
              <Panel title="Conversion Funnel">
                {contacts.length === 0 ? (
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No contacts to analyze</p>
                ) : (() => {
                  // Build funnel data with cumulative counts (each stage includes all stages at or beyond it)
                  const funnelData = stageCounts.map(({ stage, count }, idx) => {
                    // Cumulative: sum of this stage and all later stages
                    const cumulative = stageCounts.slice(idx).reduce((s, sc) => s + sc.count, 0);
                    return {
                      name: stageLabels[stage],
                      count: cumulative,
                      actual: count,
                      color: stageColors[stage],
                    };
                  });

                  return (
                    <div>
                      <ResponsiveContainer width="100%" height={260}>
                        <BarChart data={funnelData} layout="vertical" margin={{ left: 8, right: 24, top: 8, bottom: 8 }}>
                          <XAxis type="number" hide />
                          <YAxis
                            type="category"
                            dataKey="name"
                            width={120}
                            tick={{ fill: 'var(--text-secondary)', fontSize: 12 }}
                            axisLine={false}
                            tickLine={false}
                          />
                          <Tooltip
                            contentStyle={{
                              background: 'var(--bg-card)',
                              border: '1px solid var(--border-primary)',
                              borderRadius: '8px',
                              color: 'var(--text-primary)',
                              fontSize: 12,
                            }}
                            formatter={(_value: any, _name: any, props: any) => [
                              `${props.payload.actual} contacts (${totalContacts > 0 ? Math.round((props.payload.actual / totalContacts) * 100) : 0}%)`,
                              props.payload.name,
                            ]}
                          />
                          <Bar dataKey="count" radius={[0, 6, 6, 0]} barSize={28}>
                            {funnelData.map((entry, index) => (
                              <Cell key={`cell-${index}`} fill={entry.color} fillOpacity={0.85} />
                            ))}
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                      {/* Conversion rates between stages */}
                      <div className="flex flex-wrap gap-2 mt-3 px-2">
                        {funnelData.slice(0, -1).map((entry, idx) => {
                          const next = funnelData[idx + 1];
                          const rate = entry.count > 0 ? Math.round((next.count / entry.count) * 100) : 0;
                          return (
                            <div key={entry.name} className="flex items-center gap-1.5 text-xs"
                              style={{ color: 'var(--text-muted)' }}>
                              <span style={{ color: entry.color }}>{entry.name}</span>
                              <span>-&gt;</span>
                              <span style={{ color: next.color }}>{next.name}</span>
                              <span className="font-display px-1.5 py-0.5 rounded"
                                style={{ background: 'var(--bg-tertiary)', color: 'var(--text-primary)' }}>
                                {rate}%
                              </span>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })()}
              </Panel>

              {/* Status Breakdown */}
              <Panel title="Contact Status Breakdown">
                {contacts.length === 0 ? (
                  <p className="text-sm" style={{ color: 'var(--text-muted)' }}>No contacts to analyze</p>
                ) : (
                  <div className="space-y-2">
                    {Object.entries(statusMap)
                      .sort((a, b) => b[1] - a[1])
                      .map(([status, count]) => (
                        <div key={status} className="flex items-center justify-between py-1.5 px-2 rounded" style={{ background: 'var(--bg-tertiary)' }}>
                          <div className="flex items-center gap-2">
                            <span
                              className="w-2.5 h-2.5 rounded-full shrink-0"
                              style={{ background: stageColors[status] || '#6b7280' }}
                            />
                            <span className="text-sm" style={{ color: 'var(--text-primary)' }}>
                              {stageLabels[status] || status}
                            </span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-display" style={{ color: 'var(--text-primary)' }}>{count}</span>
                            <span className="text-xs" style={{ color: 'var(--text-muted)' }}>
                              ({Math.round((count / totalContacts) * 100)}%)
                            </span>
                          </div>
                        </div>
                      ))}
                  </div>
                )}
              </Panel>
            </div>
          );
        })()}

        {/* Intelligence Tab — future sprint */}
      </div>
    </div>
  );
}
